import { JwtVerifiedCredential } from '@dynamic-labs/sdk-api';
import { useDynamicContext } from '@dynamic-labs/sdk-react-core';

/**
 * Hook to access and manage user wallet information from Dynamic authentication.
 *
 * This hook provides convenient access to the user's connected wallets, including:
 * - A list of all wallet addresses
 * - The complete wallet credential objects
 * - Loading state information
 * - Special access to the Dynamic embedded wallet (if present)
 *
 * The hook abstracts away the details of extracting wallet information from
 * the Dynamic authentication context, providing a simpler interface for
 * components that need wallet data.
 *
 * @returns {Object} Object containing wallet information
 * @property {string[]} addresses - Array of wallet addresses connected to the user
 * @property {JwtVerifiedCredential[]} wallets - Array of complete wallet credential objects
 * @property {boolean} isLoading - Whether wallet information is still loading
 * @property {JwtVerifiedCredential | undefined} dynamicWallet - The Dynamic embedded wallet if present
 */
export const useWallets = (): {
  addresses: string[];
  wallets: JwtVerifiedCredential[];
  isLoading: boolean;
  dynamicWallet?: JwtVerifiedCredential;
} => {
  // Access the user object from Dynamic's authentication context
  const { user } = useDynamicContext();

  // Extract all wallet addresses from verified credentials
  // Filter out any undefined addresses and ensure type safety
  const addresses =
    user?.verifiedCredentials
      .map((wallet) => wallet.address)
      .filter((address): address is string => address !== undefined) || [];

  // Extract all wallet credential objects that have an address
  // This filters out any credentials that aren't actual wallets
  const wallets =
    (user?.verifiedCredentials.filter(
      (credential) => credential.address !== undefined,
    ) as JwtVerifiedCredential[]) || [];

  // Find the Dynamic embedded wallet if it exists
  // Dynamic's embedded wallet is identified by the 'turnkeyhd' wallet name
  const dynamicWallet = wallets.find(
    (wallet) =>
      wallet.walletName === 'turnkeyhd' || wallet.walletName === 'dynamicwaas',
  ) as JwtVerifiedCredential | undefined;

  // Return all wallet information
  // isLoading is true if the user object is not yet available
  return { addresses, wallets, isLoading: !user, dynamicWallet };
};
